import { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Box } from '@chakra-ui/react';

import { NewTabsHeader, NEW_TABS_INDEX } from './NewTabsHeader';
import { NewNextButton } from './NewNextButton';
import { type AccessDataValues } from './types';
import { AccessDataSchema, AccessDataForm, isStrongPassword } from './schemas/accessDataSchema';
import { AccessDataFields, TermsOfServiceLink } from './components';

import { TermsOfUse, TermsOfUseService } from 'src/services/termsOfUse';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';
import { Tracker } from 'src/shared/tracker/Tracker';
import { isEmpty } from 'lodash';
import { onlyDigits } from 'src/helpers/string';
import { publicApi } from 'src/publicApi';
import useLoading from 'src/helpers/useLoading';
import { getEmailValidationErrorMessage } from 'src/services/signUp';
import { trackNewSignUpContinueClick } from 'src/services/tracking/events/signup.events';

interface ValidateDataResponse {
  data: {
    password: {
      isValid: boolean;
      reason?: string;
    };
    cellphoneNumber: {
      isValid: boolean;
      reason?: string;
    };
    email: {
      isValid: boolean;
      reason?: string;
    };
  };
}

interface AccessDataProps {
  onNext: (data: AccessDataValues) => void;
  onDataChange: (data: AccessDataValues) => void;
  isStepCompleted: boolean;
  initialData: AccessDataValues;
  phoneNumber?: string;
  isLoadingPhoneNumber?: boolean;
}

export function AccessData({
  onNext,
  onDataChange,
  isStepCompleted,
  initialData,
  phoneNumber,
  isLoadingPhoneNumber = false,
}: AccessDataProps) {
  const [termsOfUse, setTermsOfUse] = useState<TermsOfUse | null>(null);
  const { openToast } = useFeedbackContext();
  const [loading, runWithLoading] = useLoading();

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setTermsOfUse(await TermsOfUseService.getTermsOfUse());
      } catch (error) {
        openToast('Erro ao buscar os Termos de Uso. Tente novamente em instantes.', {
          type: 'error-outline',
          duration: 4000,
        });
      }
    };
    fetchTerms();
  }, [openToast]);

  const formik = useFormik<AccessDataForm>({
    initialValues: {
      ...initialData,
      cellphoneNumber: phoneNumber || initialData.cellphoneNumber,
      isPasswordValid: isStrongPassword(initialData.password),
    },
    validationSchema: AccessDataSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await runWithLoading(async () => {
        try {
          const response = await publicApi.post<ValidateDataResponse>('/sign-up/user/validate-data', {
            email: values.email,
            password: values.password,
            cellphoneNumber: onlyDigits(values.cellphoneNumber),
          });

          const validationData = response.data.data;

          const hasErrors =
            !validationData.email.isValid ||
            !validationData.password.isValid ||
            !validationData.cellphoneNumber.isValid;

          if (hasErrors) {
            if (!validationData.email.isValid) {
              formik.setFieldError('email', getEmailValidationErrorMessage(validationData.email.reason));
            }
            if (!validationData.password.isValid) {
              formik.setFieldError('password', validationData.password.reason || 'Senha inválida');
            }
            if (!validationData.cellphoneNumber.isValid) {
              formik.setFieldError('cellphoneNumber', validationData.cellphoneNumber.reason || 'Celular inválido');
            }

            Tracker.trackEvent({
              event: 'erroCadastroPrescritorDadosAcesso',
              parameters: {
                emailError: validationData.email.reason,
                passwordError: validationData.password.reason,
                cellphoneError: validationData.cellphoneNumber.reason,
              },
            });

            return;
          }

          trackNewSignUpContinueClick();

          Tracker.trackEvent({
            event: 'cadastroPrescritorDadosAcessoConcluido',
            parameters: {
              email: values.email,
              'Data cadastro': new Date(),
            },
          });

          const data = {
            cellphoneNumber: values.cellphoneNumber,
            countryCode: values.countryCode,
            countryDialCode: values.countryDialCode,
            email: values.email,
            password: values.password,
          };

          onDataChange(data);
          onNext(data);
        } catch (error) {
          console.error('Erro ao validar dados:', error);
          openToast('Erro ao validar dados. Tente novamente.', {
            type: 'error-outline',
            duration: 4000,
          });
        }
      });
    },
  });

  useEffect(() => {
    const data: AccessDataValues = {
      cellphoneNumber: formik.values.cellphoneNumber,
      countryCode: formik.values.countryCode,
      countryDialCode: formik.values.countryDialCode,
      email: formik.values.email,
      password: formik.values.password,
    };
    onDataChange(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.cellphoneNumber,
    formik.values.countryCode,
    formik.values.countryDialCode,
    formik.values.email,
    formik.values.password,
  ]);

  const handlePasswordValidationChange = useCallback(
    (isValid: boolean) => {
      formik.setFieldValue('isPasswordValid', isValid);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.setFieldValue]
  );

  const handleCellphoneBlur = useCallback(() => {
    const digits = onlyDigits(formik.values.cellphoneNumber);
    if (digits.length > 0) {
      formik.validateField('cellphoneNumber');
    } else {
      formik.setFieldError('cellphoneNumber', undefined);
    }
  }, [formik]);

  const handleEmailBlur = useCallback(() => {
    if (formik.values.email.length > 0) {
      formik.validateField('email');
    } else {
      formik.setFieldError('email', undefined);
    }
  }, [formik]);

  const handlePasswordBlur = useCallback(() => {
    if (formik.values.password.length > 0) {
      formik.validateField('password');
    } else {
      formik.setFieldError('password', undefined);
    }
  }, [formik]);

  const handleSubmit = useCallback(async () => {
    const errors = await formik.validateForm();

    if (isEmpty(errors)) {
      return formik.submitForm();
    }

    Tracker.trackEvent({
      event: 'erroCadastroPrescritorDadosAcesso',
      parameters: errors,
    });
  }, [formik]);

  const handleTabClick = useCallback(
    (index: NEW_TABS_INDEX) => {
      if (index === NEW_TABS_INDEX.ACCESS_DATA) return;
      handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <form
      name="access-data"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      style={{ width: '100%' }}
    >
      <NewTabsHeader
        currentIndex={NEW_TABS_INDEX.ACCESS_DATA}
        isFirstStepCompleted={isStepCompleted}
        onTabClick={handleTabClick}
      />

      <Box>
        <AccessDataFields
          cellphoneNumber={formik.values.cellphoneNumber}
          email={formik.values.email}
          password={formik.values.password}
          cellphoneNumberError={formik.errors.cellphoneNumber}
          emailError={formik.errors.email}
          passwordError={formik.errors.password}
          onCellphoneNumberChange={formik.handleChange}
          onEmailChange={formik.handleChange}
          onPasswordChange={formik.handleChange}
          onCellphoneNumberBlur={handleCellphoneBlur}
          onEmailBlur={handleEmailBlur}
          onPasswordBlur={handlePasswordBlur}
          onPasswordValidationChange={handlePasswordValidationChange}
          isLoadingPhoneNumber={isLoadingPhoneNumber}
        />

        <TermsOfServiceLink termsOfUse={termsOfUse} />

        <NewNextButton
          currentIndex={NEW_TABS_INDEX.ACCESS_DATA}
          position={{ base: 'fixed', sm: 'static' }}
          padding={{ base: '0 20px', sm: '0' }}
          buttonText="Continuar"
          isLoading={loading}
        />
      </Box>
    </form>
  );
}
